import { useState, useEffect, useCallback } from "react";
import { formatDate } from "../utils/dates";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../components/ErrorBanner";
import { LoadingLine } from "../components/Spinner";

const ROLES = [
  "admin",
  "supervisor",
  "field_hand",
  "safety_officer",
  "consultant",
];
const ROLE_COLORS = {
  admin: "#ff4466",
  supervisor: "#00f5ff",
  field_hand: "#00cc66",
  safety_officer: "#ffaa00",
  consultant: "#0080ff",
};

// The crew roster (workflow stage 6): the rig a user works on and their
// place on the crew, for the role home screens. Shown for companies with
// rigs or hydro units (crewRoster).
const CREW_POSITIONS = ["", "toolpusher", "crew", "hydro_operator", "office"];

function UserManagement({ token, onBack, crewRoster = false }) {
  const { t } = useTranslation();
  const roleLabel = (r) => t(`users.roles.${r}`, { defaultValue: r.replace("_", " ").toUpperCase() });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "field_hand",
  });
  const [formError, setFormError] = useState("");
  const [rigs, setRigs] = useState([]);
  useEffect(() => {
    if (!crewRoster) return;
    axios
      .get(process.env.REACT_APP_API_URL + "/api/rigs", { headers: { Authorization: "Bearer " + token } })
      .then((r) => setRigs(Array.isArray(r.data) ? r.data : r.data?.data || []))
      .catch(() => setRigs([]));
  }, [crewRoster, token]);
  const rigName = (id) => {
    const r = rigs.find((x) => String(x._id) === String(id));
    return r ? [r.rigNumber, r.name].filter(Boolean).join(" — ") : "";
  };
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: "Bearer " + token };
  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";

  const fetchUsers = useCallback(async () => {
    const headers = { Authorization: "Bearer " + token };
    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/api/users",
        { headers },
      );
      setUsers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t("users.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  function openCreate() {
    setEditUser(null);
    setForm({ name: "", email: "", password: "", role: "field_hand" });
    setFormError("");
    setShowForm(true);
  }
  function openEdit(u) {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, assignedRigId: u.assignedRigId || "", crewPosition: u.crewPosition || "" });
    setFormError("");
    setShowForm(true);
  }

  async function saveUser() {
    setSaving(true);
    try {
      if (editUser) {
        const updates = { name: form.name, role: form.role };
        if (form.password) updates.password = form.password;
        if (crewRoster) {
          updates.assignedRigId = form.assignedRigId || null;
          updates.crewPosition = form.crewPosition || "";
        }
        await axios.put(
          process.env.REACT_APP_API_URL + "/api/users/" + editUser._id,
          updates,
          { headers },
        );
      } else {
        await axios.post(process.env.REACT_APP_API_URL + "/api/users", form, {
          headers,
        });
      }
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.error || t("users.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u) {
    try {
      await axios.put(
        process.env.REACT_APP_API_URL + "/api/users/" + u._id,
        { active: !u.active },
        { headers },
      );
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || t("users.statusFailed"));
    }
  }

  async function deleteUser(u) {
    if (!window.confirm(t("users.confirmDelete", { name: u.name }))) return;
    try {
      await axios.delete(
        process.env.REACT_APP_API_URL + "/api/users/" + u._id,
        { headers },
      );
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || t("users.deleteFailed"));
    }
  }

  const inputStyle = {
    width: "100%",
    backgroundColor: "rgba(0,245,255,0.05)",
    border: "1px solid rgba(0,245,255,0.2)",
    borderRadius: "6px",
    padding: "10px 14px",
    color: "white",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle = {
    color: cyan,
    fontSize: "10px",
    letterSpacing: "2px",
    display: "block",
    marginBottom: "6px",
    fontWeight: "700",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: "1px solid rgba(0,245,255,0.15)",
          padding: "14px 16px",
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "1px solid rgba(0,245,255,0.15)",
              color: cyan,
              cursor: "pointer",
              fontSize: "11px",
              padding: "5px 14px",
              borderRadius: "4px",
              letterSpacing: "1px",
              whiteSpace: "nowrap",
            }}
          >
            {t("users.back")}
          </button>
          <div
            style={{ width: "1px", height: "20px", backgroundColor: border }}
          />
          <div
            style={{
              color: cyan,
              fontSize: "13px",
              fontWeight: "700",
              letterSpacing: "3px",
            }}
          >
            {t("users.title")}
          </div>
        </div>
        <button
          onClick={openCreate}
          style={{
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            color: "#020817",
            fontWeight: "700",
            fontSize: "11px",
            letterSpacing: "2px",
            padding: "8px 20px",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
          }}
        >
          {t("users.addUser")}
        </button>
      </nav>

      <main style={{ padding: "24px 16px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px" }}>
          <h2
            style={{
              fontSize: "22px",
              fontWeight: "800",
              letterSpacing: "2px",
              color: "white",
              marginBottom: "4px",
            }}
          >
            {t("users.heading")}
          </h2>
          <p
            style={{
              color: "rgba(0,245,255,0.5)",
              fontSize: "12px",
              letterSpacing: "3px",
            }}
          >
            {t("users.registered", { count: users.length })}
          </p>
        </div>

        {error && <ErrorBanner message={error} onRetry={fetchUsers} />}

        {showForm && (
          <div
            style={{
              backgroundColor: "rgba(0,0,0,0.85)",
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "#020817",
                border: "1px solid rgba(0,245,255,0.15)",
                borderRadius: "12px",
                padding: "32px",
                width: "100%",
                maxWidth: "440px",
                boxShadow: "0 0 60px rgba(0,245,255,0.1)",
              }}
            >
              <div
                style={{
                  color: cyan,
                  fontSize: "12px",
                  fontWeight: "700",
                  letterSpacing: "3px",
                  marginBottom: "24px",
                }}
              >
                {editUser ? t("users.editUser") : t("users.newUser")}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div>
                  <label style={labelStyle} htmlFor="um-name">{t("users.fullName")}</label>
                  <input
                    id="um-name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder={t("users.namePlaceholder")}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="um-email">{t("users.email")}</label>
                  <input
                    id="um-email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder={t("users.emailPlaceholder")}
                    disabled={!!editUser}
                    style={{
                      ...inputStyle,
                      opacity: editUser ? 0.5 : 1,
                      cursor: editUser ? "not-allowed" : "text",
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="um-password">
                    {editUser ? t("users.newPassword") : t("users.password")}
                  </label>
                  <input
                    id="um-password"
                    type="password"
                    value={form.password}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, password: e.target.value }))
                    }
                    placeholder="••••••••"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="um-role">{t("users.role")}</label>
                  <select
                    id="um-role"
                    value={form.role}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, role: e.target.value }))
                    }
                    style={{ ...inputStyle, backgroundColor: "#020817" }}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                </div>
                {crewRoster && editUser && (
                  <>
                    <div>
                      <label style={labelStyle} htmlFor="um-rig">{t("users.assignedRig")}</label>
                      <select id="um-rig" data-testid="um-rig" value={form.assignedRigId || ""} onChange={(e) => setForm((f) => ({ ...f, assignedRigId: e.target.value }))} style={{ ...inputStyle, backgroundColor: "#020817" }}>
                        <option value="">{t("users.noRig")}</option>
                        {rigs.map((r) => (
                          <option key={r._id} value={r._id}>
                            {[r.rigNumber, r.name].filter(Boolean).join(" — ")}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor="um-position">{t("users.crewPosition")}</label>
                      <select id="um-position" data-testid="um-position" value={form.crewPosition || ""} onChange={(e) => setForm((f) => ({ ...f, crewPosition: e.target.value }))} style={{ ...inputStyle, backgroundColor: "#020817" }}>
                        {CREW_POSITIONS.map((p) => (
                          <option key={p || "none"} value={p}>
                            {t(`users.positions.${p || "none"}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              {formError && (
                <div
                  style={{
                    color: "#ff4466",
                    fontSize: "12px",
                    marginTop: "12px",
                  }}
                >
                  ⚠ {formError}
                </div>
              )}
              <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                <button
                  onClick={() => setShowForm(false)}
                  style={{
                    flex: 1,
                    background: "none",
                    border: "1px solid rgba(0,245,255,0.15)",
                    color: "rgba(0,245,255,0.5)",
                    padding: "10px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "11px",
                    letterSpacing: "2px",
                  }}
                >
                  {t("users.cancel")}
                </button>
                <button
                  onClick={saveUser}
                  disabled={saving}
                  style={{
                    flex: 2,
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    letterSpacing: "2px",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? t("users.saving") : editUser ? t("users.saveChanges") : t("users.createUser")}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingLine label={t("users.loading")} />
        ) : users.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 0",
              color: "rgba(0,245,255,0.3)",
              fontSize: "12px",
              letterSpacing: "3px",
            }}
          >
            {t("users.empty")}
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {users.map((u) => (
              <div
                key={u._id}
                style={{
                  backgroundColor: card,
                  border:
                    "1px solid " + (u.active ? border : "rgba(255,68,102,0.2)"),
                  borderRadius: "10px",
                  padding: "16px 20px",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "16px",
                  opacity: u.active ? 1 : 0.6,
                  transition: "opacity 0.2s",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, rgba(0,245,255,0.1), rgba(0,128,255,0.1))",
                    border: "1px solid rgba(0,245,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: cyan,
                    fontWeight: "700",
                    fontSize: "16px",
                    flexShrink: 0,
                  }}
                >
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "4px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        fontWeight: "700",
                        fontSize: "14px",
                      }}
                    >
                      {u.name}
                    </span>
                    <span
                      style={{
                        backgroundColor: (ROLE_COLORS[u.role] || cyan) + "22",
                        color: ROLE_COLORS[u.role] || cyan,
                        fontSize: "9px",
                        fontWeight: "700",
                        padding: "2px 8px",
                        borderRadius: "20px",
                        border: "1px solid " + (ROLE_COLORS[u.role] || cyan),
                        letterSpacing: "1px",
                      }}
                    >
                      {roleLabel(u.role)}
                    </span>
                    {!u.active && (
                      <span
                        style={{
                          backgroundColor: "rgba(255,68,102,0.15)",
                          color: "#ff4466",
                          fontSize: "9px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "20px",
                          border: "1px solid rgba(255,68,102,0.4)",
                          letterSpacing: "1px",
                        }}
                      >
                        {t("users.inactive")}
                      </span>
                    )}
                  </div>
                  <div
                    style={{ color: "rgba(0,245,255,0.4)", fontSize: "11px", overflowWrap: "anywhere" }}
                  >
                    {u.email}
                  </div>
                  {crewRoster && (u.assignedRigId || u.crewPosition) && (
                    <div data-testid="um-roster" style={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", marginTop: "2px" }}>
                      {t("users.onRig", { rig: rigName(u.assignedRigId) || t("users.noRig"), position: t(`users.positions.${u.crewPosition || "none"}`) })}
                    </div>
                  )}
                  <div
                    style={{
                      color: "rgba(255,255,255,0.2)",
                      fontSize: "10px",
                      marginTop: "2px",
                    }}
                  >
                    {t("users.joined", {
                      date: formatDate(u.createdAt),
                    })}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
                  <button
                    onClick={() => openEdit(u)}
                    style={{
                      background: "none",
                      border: "1px solid rgba(0,245,255,0.2)",
                      color: "rgba(0,245,255,0.6)",
                      fontSize: "10px",
                      padding: "5px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("users.edit")}
                  </button>
                  <button
                    onClick={() => toggleActive(u)}
                    style={{
                      background: "none",
                      border:
                        "1px solid " +
                        (u.active
                          ? "rgba(255,170,0,0.4)"
                          : "rgba(0,204,102,0.4)"),
                      color: u.active ? "#ffaa00" : "#00cc66",
                      fontSize: "10px",
                      padding: "5px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      letterSpacing: "1px",
                    }}
                  >
                    {u.active ? t("users.deactivate") : t("users.activate")}
                  </button>
                  <button
                    onClick={() => deleteUser(u)}
                    style={{
                      background: "none",
                      border: "1px solid rgba(255,68,102,0.4)",
                      color: "rgba(255,68,102,0.7)",
                      fontSize: "10px",
                      padding: "5px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("users.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default UserManagement;
